import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Divider, Alert, LinearProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { attachmentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ALLOWED = ['txt', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'csv'];
const MAX_BYTES = 10 * 1024 * 1024;

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const fileIcon = (name, mime) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if ((mime && mime.startsWith('image/')) || ['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return <ImageIcon color="primary" />;
  if (ext === 'pdf') return <PictureAsPdfIcon color="error" />;
  return <InsertDriveFileIcon color="action" />;
};

/**
 * Attachment upload + list for a task (drag & drop). Critical for bug reports
 * (screenshots, logs). Works for internal and client users; backend scopes and
 * IDOR-protects every endpoint.
 */
function TaskAttachments({ taskId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await attachmentsAPI.getAll(taskId);
      setItems(res.data || []);
    } catch (err) {
      console.error('[ATTACH] load error', err);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const validate = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED.includes(ext)) return `Dateityp .${ext} nicht erlaubt`;
    if (file.size > MAX_BYTES) return `Datei zu groß (max. 10 MB)`;
    return null;
  };

  const uploadFiles = async (files) => {
    setError(null);
    const list = Array.from(files);
    setUploading(true);
    try {
      for (const file of list) {
        const v = validate(file);
        if (v) { setError(v); continue; }
        try {
          await attachmentsAPI.upload(taskId, file);
        } catch (err) {
          setError(err.response?.data?.error || `Upload fehlgeschlagen: ${file.name}`);
        }
      }
      await load();
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files);
  };

  const handleDownload = async (att) => {
    try {
      const res = await attachmentsAPI.download(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.mime_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_filename || att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download fehlgeschlagen');
    }
  };

  const remove = async (id) => {
    try {
      await attachmentsAPI.delete(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AttachFileIcon color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Anhänge{items.length ? ` (${items.length})` : ''}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Drop zone */}
      <Box
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          bgcolor: dragOver ? 'action.hover' : 'transparent',
          borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer', mb: 2,
          transition: 'all 0.15s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => { if (e.target.files.length) uploadFiles(e.target.files); e.target.value = ''; }}
        />
        <CloudUploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          Datei hierher ziehen oder <strong>klicken zum Auswählen</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Bilder, PDF, Office, ZIP, CSV · max. 10 MB
        </Typography>
      </Box>

      {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Keine Anhänge.</Typography>
      ) : (
        <List dense>
          {items.map((att) => {
            const mine = user && att.user && att.user.id === user.id;
            return (
              <ListItem
                key={att.id}
                secondaryAction={
                  <Box>
                    <IconButton edge="end" title="Herunterladen" onClick={() => handleDownload(att)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    {mine && (
                      <IconButton edge="end" title="Löschen" onClick={() => remove(att.id)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    )}
                  </Box>
                }
              >
                <ListItemIcon sx={{ minWidth: 38 }}>{fileIcon(att.original_filename, att.mime_type)}</ListItemIcon>
                <ListItemText
                  primary={att.original_filename || att.filename}
                  secondary={`${formatSize(att.file_size)} · ${att.user?.full_name || att.user?.username || ''}`}
                  primaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-all' } }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}

export default TaskAttachments;
